import { Trash, Warning } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { Ref } from 'react';
import translations from '../../../../../../../translations';
import type { ListFilter } from '../../../../../../../types/generic';
import type { ComposerElement, PreviewMode } from '../../../../../../../types/pageEditor';
import ButtonIcon from '../../../../../../buttons/ButtonIcon';
import { componentDataSourceFilter, isComponentConfigured } from '../../../../../utils';
import { usePageEditor } from '../../../../PageEditorProvider';
import { useComposer } from '../../ComposerProvider';

interface Props {
    config: ComposerElement;
    classes: string;
    drag: Ref<HTMLDivElement> | null;
    id: string;
    parentId: string | null | undefined;
    preview: PreviewMode | null;
}

const PageComponent = ({ config, classes, drag, id, preview }: Props) => {
    const { componentType, developerName } = config;
    const { setElementToDelete } = useComposer();
    const { getComponent } = usePageEditor();
    const { renderFn, ui } = getComponent(componentType?.toUpperCase() as string);

    const isComponentConfigValid = isComponentConfigured(config, getComponent);

    const { isConfigured: isComponentFilterConfigValid } = componentDataSourceFilter(
        config?.objectDataRequest?.listFilter as ListFilter,
    );

    const headerClasses = classNames({
        'page-container-header': true,
        preview: !!preview,
    });

    const componentTypeLabel = ui.caption;
    const componentTitle = developerName
        ? `${componentTypeLabel}: ${developerName}`
        : `New ${componentTypeLabel}`;

    const isDraggable = !preview;

    return (
        // biome-ignore lint/a11y/useSemanticElements: Requires dedicated refactor to use correct element
        <div
            id={id}
            role="button"
            ref={isDraggable ? drag : null}
            tabIndex={-1}
            className="page-container-inner"
        >
            <div className={headerClasses}>
                <span title={componentTitle} className="page-element-title">
                    {componentTitle}
                </span>
                <div className="container-header-actions flex-child-right">
                    <ButtonIcon
                        className="header-icon-button"
                        title="Delete Component"
                        onClick={() => setElementToDelete({ targetId: id })}
                    >
                        <Trash size={20} weight="fill" />
                    </ButtonIcon>
                </div>
            </div>
            <div className={classes}>{renderFn({ ...config })}</div>
            {!(isComponentConfigValid && isComponentFilterConfigValid) && (
                <mark
                    title={translations.PAGE_BUILDER_unconfigured_component}
                    className="config-warning"
                    data-testid="mark"
                >
                    <Warning weight="fill" />
                </mark>
            )}
        </div>
    );
};

export default PageComponent;
